<script lang="ts" setup>
import { computed, type PropType } from 'vue'
import UpgradeIntermediate from '~/pages/upgrade-intermediate.vue'
const { hasIntermediateAccess } = useAuth();

interface RecipeOverviewItem {
  holeNo: number
  recipeId: number
  recipeName: string
  startTime: string | Date | null
  stopTime: string | Date | null
  totalTime: string
  totalTimeMs: number
  volume: number
  cement: number
  accelerator: number
}

const props = defineProps({
  recipeOverviewData: {
    type: Array as PropType<RecipeOverviewItem[]>,
    required: true
  }
})

const searchQuery = ref('')


const formatTime = (date: any) => {
  if (!date) return "--:--:--";
  return new Date(date).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

const formatDate = (date: any) => {
  if (!date) return "--";
  const d = new Date(date);
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatNumber = (num: number, decimals: number = 2) => {
  return num !== undefined ? num.toLocaleString(undefined, { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  }) : '0,00'
}

const formatDurationMs = (ms: number) => {
  const seconds = Math.floor(ms / 1000)
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}

const filteredOverviewRecipes = computed(() => {
  let data = props.recipeOverviewData
  if (!searchQuery.value) return data
  
  const q = searchQuery.value.toLowerCase()
  return data.filter(r => 
    (r.recipeName && r.recipeName.toLowerCase().includes(q)) ||
    (r.holeNo && r.holeNo.toString().includes(q))
  )
})

const filterConsumptionPerRecipeStats = computed(() => {
  let data = filteredOverviewRecipes.value;
  const grouped = new Map();

  for (const item of data) {
      const key = item.recipeId;
      if (!grouped.has(key)) {
          grouped.set(key, {
              recipeId: key,
              recipeName: item.recipeName,
              cement: 0,
              accelerator: 0,
              totalMs: 0
          });
      }
      const group = grouped.get(key);
      group.cement += (item.cement || 0);
      group.accelerator += (item.accelerator || 0);
      
      if (item.totalTimeMs) {
          group.totalMs += item.totalTimeMs;
      }            
  }

  return Array.from(grouped.values()).map(item => {
    return {
      name: item.recipeName,
      durationMs: item.totalMs,
      cement: item.cement,
      accelerator: item.accelerator
    }
  }).sort((a, b) => b.cement - a.cement)
})

const totalStats = computed(() => {
  const data = filteredOverviewRecipes.value
  const totalMs = data.reduce((acc, curr) => acc + curr.totalTimeMs, 0)
  const totalCement = data.reduce((acc, curr) => acc + curr.cement, 0)
  const totalAccelerator = data.reduce((acc, curr) => acc + curr.accelerator, 0)
  return {  
    runs: filteredOverviewRecipes.value.length,
    duration: formatDurationMs(totalMs),
    cement: formatNumber(totalCement, 1),
    accelerator: formatNumber(totalAccelerator, 1)
  }
})

const headers = [
  { title: 'Hole', key: 'holeNo', align: 'start' as const },
  { title: 'Recipe', key: 'recipeName' },
  { title: 'Start', key: 'startTime' },
  { title: 'Stop', key: 'stopTime' },
  { title: 'Duration', key: 'totalTime' },
  { title: 'Cement (kg)', key: 'cement', align: 'end' as const },
  { title: 'Accel. (kg)', key: 'accelerator', align: 'end' as const },
  { title: 'Volume (L)', key: 'volume', align: 'end' as const },
];
</script>

<template>
  <UpgradeIntermediate v-if="!hasIntermediateAccess" style="min-height: 400px;" />
  <v-container fluid v-else>
    <v-row>
      <!-- Total Consumption -->
      <v-col cols="12" md="6">
        <v-card class="h-100" elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
            Total Consumption
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <v-row dense>
              <v-col cols="6" sm="6">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Total Runs</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ totalStats.runs }}</div>
              </v-col>
              <v-col cols="6" sm="6">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Total Duration</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ totalStats.duration }}</div>
              </v-col>
              <v-col cols="6" sm="6" class="mt-4">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Total Cement</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ totalStats.cement }} <span class="text-caption text-medium-emphasis">kg</span></div>
              </v-col>
              <v-col cols="6" sm="6" class="mt-4">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Total Accelerator</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ totalStats.accelerator }} <span class="text-caption text-medium-emphasis">kg</span></div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Consumption per Recipe -->
      <v-col cols="12" md="6">
        <v-card class="h-100" elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
            Consumption per Recipe
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text style="max-height: 300px; overflow-y: auto;">
            <div v-for="r in filterConsumptionPerRecipeStats" :key="r.name" class="mb-3 pa-3 bg-grey-lighten-5 rounded border">
              <div class="text-subtitle-2 font-weight-bold mb-2 text-blue-grey-darken-3">{{ r.name }}</div>
              <div class="d-flex justify-space-between">
                <div class="d-flex flex-column align-end">
                  <span class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Cement</span>
                  <span class="font-weight-bold font-monospace">{{ formatNumber(r.cement, 1) }} kg</span>
                </div>
                <div class="d-flex flex-column align-end">
                  <span class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Accel.</span>
                  <span class="font-weight-bold font-monospace">{{ formatNumber(r.accelerator, 1) }} kg</span>
                </div>
                <div class="d-flex flex-column align-end">
                  <span class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Time</span>
                  <span class="font-weight-bold font-monospace">{{ formatDurationMs(r.durationMs) }}</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Recipe Overview Table -->
    <v-card class="mt-6" elevation="2">
      <v-card-title class="d-flex flex-wrap justify-space-between align-center py-3 px-4 bg-grey-lighten-4">
        <div class="d-flex align-center mb-2 mb-sm-0">
          <span class="text-subtitle-1 font-weight-bold mr-3">Recipe Overview</span>
          <v-chip size="small" variant="flat" color="blue-grey-lighten-5" class="text-blue-grey-darken-2 font-weight-bold">
            {{ filteredOverviewRecipes.length }}
          </v-chip>
        </div>
        <div class="d-flex align-center flex-wrap gap-4">
          <v-text-field
            v-model="searchQuery"
            placeholder="Filter by Recipe or Hole..."
            density="compact"
            variant="outlined"
            hide-details
            prepend-inner-icon="mdi-magnify"
            style="min-width: 250px;"
            bg-color="white"
          ></v-text-field>
        </div>
      </v-card-title>
      <v-divider></v-divider>
      
      <v-data-table
        :headers="headers"
        :items="filteredOverviewRecipes"
        density="compact"
        class="text-body-2"
        hover
      >
        <template v-slot:item.holeNo="{ item }">
          <v-chip size="small" variant="flat" color="blue-lighten-5" class="text-blue-darken-2 font-weight-bold">
            {{ item.holeNo }}
          </v-chip>
        </template>

        <template v-slot:item.recipeName="{ item }">
          <div class="d-flex flex-column py-1">
            <span class="font-weight-bold text-body-2">{{ item.recipeName || 'Unknown' }}</span>
            <v-chip size="x-small" color="grey-darken-3" variant="flat" class="font-weight-bold align-self-start mt-1">
              ID: {{ item.recipeId }}
            </v-chip>
          </div>
        </template>

        <template v-slot:item.startTime="{ item }">
          <div class="d-flex flex-column py-1">
              <span class="text-caption text-medium-emphasis">{{ formatDate(item.startTime) }}</span>
              <span class="text-body-2 font-weight-medium">{{ formatTime(item.startTime) }}</span>
          </div>
        </template>

        <template v-slot:item.stopTime="{ item }">
          <div class="d-flex flex-column py-1">
              <span class="text-caption text-medium-emphasis">{{ formatDate(item.stopTime) }}</span>
              <span class="text-body-2 font-weight-medium">{{ formatTime(item.stopTime) }}</span>
          </div>
        </template>

        <template v-slot:item.totalTime="{ item }">
           <div class="d-flex align-center font-weight-medium">
              <v-icon size="small" class="mr-1 text-medium-emphasis">mdi-timer-outline</v-icon>
              {{ formatDurationMs(item.totalTimeMs) }}
           </div>
        </template>

        <template v-slot:item.cement="{ item }"><span class="font-monospace">{{ formatNumber(item.cement, 1) }}</span></template>
        <template v-slot:item.accelerator="{ item }"><span class="font-monospace">{{ formatNumber(item.accelerator, 1) }}</span></template>
        
        <template v-slot:item.volume="{ item }">
          <span class="font-weight-bold text-high-emphasis bg-blue-lighten-5 px-2 py-1 rounded font-monospace">
            {{ formatNumber(item.volume, 1) }}
          </span>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
/* Utility class for monospace font */
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>